/**
 * Conteos de caja corregidos.
 *
 * The owner, 2026-10-14, after a cashier opened a till typing C$ 10,100 and its corte found
 * C$ 10,250 missing: «is there any way to correct that till opening and till closing amount as the
 * owner?». A count is a fact of the log and stays as it was typed. A correction says what was really
 * counted, and the books move from what the count did to what it should have done:
 *
 * - An opening (`caja.apertura_corregida`). With nothing handed on from a corte, the whole count
 *   was the float, brought from general cash: the float follows it, back to general cash or out of
 *   it (`todoDelGeneral`). After a corte that left a float, or once the opener said what they put
 *   in and counted something else, the count was held against that: the opening's difference
 *   follows it. Dollars always came from general cash. Once the till has closed, its corte expected
 *   the float the opening said, so what the corte found over or short moves the other way, and the
 *   drawer, which the corte already counted, does not move.
 * - A corte (`caja.corte_corregido`): what it found over or short, what it handed over (to general
 *   cash in an owner's hands, or to «Efectivo por recibir»), and what it left in the drawer. Once
 *   a shift has opened on the drawer, that opening was held against what was left, and moves with
 *   it (2026-10-15, the owner: «why can't i correct "amount left in till"»): what it expected, and
 *   so what it found over or short. The drawer it counted does not move.
 *
 * General cash counted after the count (`conteoAlcanzado`) already found what the float or the
 * handover really was, so general cash does not move a second time: what that count found moves the
 * other way instead (`reencontrar`), on «Sobrante» or «Faltante de efectivo general», each once, or
 * on capital when it was general cash's first count. A purchase's corrections do the same
 * (`compras.ts`). The live shop's 12 October corte, corrected on 2026-10-15 before this, had put a
 * shortage general cash's count found on its surplus, as C$ -783.
 *
 * Every correction is held to its own effects before it is booked (`noCuadraLaCorreccion`): the
 * drawer, what is waiting to be handed over and the dollars move as the tills do, and the over/short
 * accounts as the counts it changes now find. One that does not is refused, and nothing of it is
 * booked.
 *
 * What each count found over or short, as it stands, the books keep themselves
 * (`conteosCorregidos`): its own entries net it, and the corrections after it do not. It is what a
 * shortage explained may take (`faltante.explicado`) and what «Control» puts on whoever had the
 * drawer. A count's total moves from what it was to what it is: «Sobrante de caja» and «Faltante de
 * caja» give up what it put on them, and take what it should have, each once (`reexpresar`).
 *
 * Refused, for the screen to say in place of the form: a route's till; one opened before the float
 * was on the books; an opening once the drawer changed hands (the handover counted it); a corte's
 * float changed once a shift has opened on it, unless the correction names that opening; what was
 * handed over changed once the owner received it (that count found it); and a correction that would
 * leave a shortage smaller than what the owner already explained of it.
 */

import {
  centavos,
  esCamion,
  fechaEnNicaragua,
  fxRate,
  orderKey,
  usdToCordobas,
  type AnyDomainEvent,
  type Envelope,
  type Settings,
  type StoredEnvelope,
} from '@abastece/shared';
import { ACCOUNTS, type AccountKey } from '../ledger/accounts.js';
import { credit, debit, entry, type JournalEntry, type JournalLine } from '../ledger/journal.js';
import type { ApplyResult, ConteoDeCaja, Effect, Reader, SesionCaja } from '../state.js';
import { lugarEnElLog } from './contadas.js';
import { noCuadraLaCorreccion, type ConteoQueCambia } from './cuadran.js';
import {
  conteoAlcanzado,
  corregidos,
  DE_LA_CAJA,
  DEL_GENERAL,
  explicadoDe,
  explicadoDeMas as deMas,
  reencontrar,
  reexpresarEn,
  totalEn,
  type ConteoAlcanzado,
  type Reencontrado,
} from './encontrado.js';

/** Why a till's count cannot be corrected so, for the screen to say in its own language. */
export type PorQueElConteoNo =
  | 'deRuta'
  | 'antigua'
  | 'entregada'
  | 'sinCorte'
  | 'igual'
  | 'quedaDeMas'
  | 'relevada'
  | 'recibida'
  | 'explicada';

/** What the books know of a till when one of its counts is corrected (`datosDeLaCaja`). */
export interface CajaACorregir {
  sesion: SesionCaja;
  /** The opening's place in the log; null when only the corte is being corrected. */
  lugarApertura: number | null;
  /** The corte's place in the log; null while the till is open. */
  lugarCorte: number | null;
  /** What the corte found over (positive) or short (negative), in córdobas, as it stands. Null: open. */
  totalCorte: number | null;
  /** What the owner explained of each count's shortage (`faltante.explicado`), voided ones aside. */
  explicadoApertura: number;
  explicadoCorte: number;
  /** Where the corte took the day's money: an owner's hands (general cash), or «Efectivo por recibir». */
  destinoCorte: 'general' | 'porRecibir' | null;
  /**
   * General cash's first count after the opening, and after the corte: it found what the float or
   * the handover really was, so a correction moves what it found instead (`encontrado.ts`).
   */
  generalApertura: ConteoAlcanzado | null;
  generalCorte: ConteoAlcanzado | null;
  /** A shift has opened on the drawer since the corte, taking over what it left. */
  relevada: boolean;
  /**
   * That shift's opening, as whoever corrects the corte names it: its till, the opening's place in
   * the log, and what the owner explained of what it found missing. Null when none has opened, or
   * none was named.
   */
  siguiente: { sesion: SesionCaja; id: string; lugar: number; explicado: number } | null;
}

/** Money moved by a correction, in each currency and in córdobas (dollars at the official rate). */
export interface Movido {
  cordobas: number;
  /** USD cents. */
  dolares: number;
  valor: number;
}

/** What correcting a till's count does, worked out before anything changes: the books and the sheet read the same. */
export interface CorreccionDeConteo {
  /** The till's row as it becomes. */
  patch: Partial<SesionCaja>;
  /** What the opening found over or short, before and after. */
  apertura: { antes: number; despues: number };
  /** The same of the corte; null while the till is open. */
  corte: { antes: number; despues: number } | null;
  /**
   * The opening that took over the drawer, when what the corte left changes: its till's row as it
   * becomes, and what it found over or short, before and after. Null otherwise.
   */
  siguiente: { sesionId: string; aperturaId: string; patch: Partial<SesionCaja>; antes: number; despues: number } | null;
  /**
   * General cash's count since, when one came after: the money it already found stays where it is,
   * and what it found moves (`reencontrar`), before and after. Null when none came after.
   */
  general: Reencontrado | null;
  /** Into general cash (negative: more out of it); `contada`, general cash counted since, whose count takes it. */
  aGeneral: Movido & { contada: boolean };
  /** Into «Efectivo por recibir», on this till, for the owner to receive. */
  aPorRecibir: Movido;
  /** The entry. Empty: nothing moves on the books. */
  lineas: JournalLine[];
}

/** Why not, for the books; `por`, for the screen; and, a shortage already explained, by how much. */
export type RechazoDeConteo = { rechazo: string; por: PorQueElConteoNo; explicado?: number; faltaria?: number };
type Rechazo = RechazoDeConteo;

const NADA: Movido = { cordobas: 0, dolares: 0, valor: 0 };

/** What a count's own entries put on «Sobrante de caja» (positive) and «Faltante de caja» (negative). */
export function totalDeAsientos(asientos: readonly JournalEntry[]): number {
  return totalEn(asientos, DE_LA_CAJA);
}

/**
 * Where a corte took the day's money, by its own entry: general cash, or «Efectivo por recibir».
 * One that handed nothing booked no entry: in an owner's hands if one took it as it closed.
 */
export function destinoDelCorte(sesion: SesionCaja, asientos: readonly JournalEntry[]): 'general' | 'porRecibir' {
  for (const a of asientos) {
    for (const l of a.lineas) {
      if (l.debe > 0 && l.cuenta === ACCOUNTS.CAJA_FUERTE.code) return 'general';
      if (l.debe > 0 && l.cuenta === ACCOUNTS.EFECTIVO_POR_RECIBIR.code) return 'porRecibir';
    }
  }
  const enMano = sesion.recibidoPor && sesion.recibidoEn && sesion.cerradaEn && Date.parse(sesion.recibidoEn) === Date.parse(sesion.cerradaEn);
  return enMano ? 'general' : 'porRecibir';
}

/**
 * A count's over or short moved from `antes` to `despues` (positive: over): what it put on
 * «Sobrante de caja» or «Faltante de caja» comes off, and what it should have put goes on, each
 * account once. The lines come to `despues - antes` on the credit side.
 */
export function reexpresar(antes: number, despues: number): JournalLine[] {
  return reexpresarEn(DE_LA_CAJA, antes, despues);
}

/** A signed amount on one account: a debit when positive, a credit when negative, nothing at zero. */
function linea(cuenta: AccountKey, monto: number, memo?: string): JournalLine[] {
  if (monto > 0) return [debit(cuenta, centavos(monto), memo)];
  if (monto < 0) return [credit(cuenta, centavos(-monto), memo)];
  return [];
}

/**
 * Money into general cash (positive) or out of it: the account itself, or, once general cash was
 * counted since, what that count found, the other way (`reencontrar`).
 */
function alGeneral(valor: number, conteo: ConteoAlcanzado | null): Reencontrado | Rechazo | null {
  if (valor === 0 || !conteo) return null;
  const r = reencontrar(conteo, valor, 'el conteo del efectivo general', 'Lo encontró el conteo del efectivo general');
  return 'rechazo' in r ? { ...r, por: 'explicada' } : r;
}

function lineasAlGeneral(valor: number, general: Reencontrado | null): JournalLine[] {
  return general ? general.lineas : linea('CAJA_FUERTE', valor, valor > 0 ? 'Al efectivo general' : 'Del efectivo general');
}

/**
 * Whether an opening took its whole count from general cash, so that correcting the count moves the
 * float and not a difference: nothing handed on from a corte, and nothing counted apart from what
 * came. An opening asked what was put in for change (2026-10-14) that counted something else held
 * the count against what was put in, as after a corte that left a float: its difference moves.
 */
export function todoDelGeneral(s: SesionCaja): boolean {
  return s.esperadoApertura != null && s.esperadoApertura === s.deGeneral && (s.diferenciaApertura ?? 0) === 0;
}

/** A shortage left smaller than what the owner already explained of it. */
function explicadoDeMas(faltante: number, explicado: number, cual: string): Rechazo | null {
  const no = deMas(faltante, explicado, cual);
  return no ? { ...no, por: 'explicada' } : null;
}

/**
 * What correcting a till's opening to `nuevo` does (`caja.apertura_corregida`): córdobas and
 * dollars (USD cents) really in the drawer when it opened.
 */
export function corregirApertura(
  caja: CajaACorregir,
  nuevo: { cordobas: number; dolares: number },
  config: Settings,
): CorreccionDeConteo | Rechazo {
  const no = (por: PorQueElConteoNo, rechazo: string): Rechazo => ({ rechazo, por });
  const s = caja.sesion;
  if (esCamion(s.ubicacion)) return no('deRuta', 'La caja de una ruta se cuadra al liquidarla');
  if (s.esperadoApertura == null || s.deGeneral == null) {
    return no('antigua', 'Esa caja se abrió antes de que el fondo se llevara en los libros');
  }
  if (s.entregadaEn != null || s.entregadaRev != null) {
    return no('entregada', 'Esa caja cambió de manos después de abrirse: la entrega ya contó lo que había');
  }
  const cerrada = s.estado === 'cerrada';
  if (cerrada && caja.totalCorte === null) return no('sinCorte', 'Esa caja ya se cerró: la corrección lleva su corte');

  const dF = nuevo.cordobas - s.fondoInicial;
  const dFD = nuevo.dolares - (s.fondoDolares ?? 0);
  if (dF === 0 && dFD === 0) return no('igual', 'Ese ya es el conteo de la apertura');

  // Nothing handed on from a corte: the whole count was the float, brought from general cash.
  const todoDeGeneral = todoDelGeneral(s);
  const dFDV = usdToCordobas(centavos(dFD), fxRate(config.fxTasaOficial));
  const dG = todoDeGeneral ? dF : 0;
  const antes = s.diferenciaApertura ?? 0;
  const apertura = { antes, despues: todoDeGeneral ? antes : antes + dF };
  const corte = cerrada ? { antes: caja.totalCorte!, despues: caja.totalCorte! - dF - dFDV } : null;

  const enApertura = explicadoDeMas(Math.max(-apertura.despues, 0), caja.explicadoApertura, 'la apertura');
  if (enApertura) return enApertura;
  const enCorte = corte ? explicadoDeMas(Math.max(-corte.despues, 0), caja.explicadoCorte, 'el corte') : null;
  if (enCorte) return enCorte;

  const valor = 0 - (dG + dFDV);
  const general = alGeneral(valor, caja.generalApertura);
  if (general && 'rechazo' in general) return general;
  const lineas: JournalLine[] = [
    ...lineasAlGeneral(valor, general),
    // An open drawer holds what the count says; a closed one was counted at its corte, and stays.
    ...(cerrada ? [] : [...linea('CAJA_CORDOBAS', dF, 'Fondo corregido'), ...linea('CAJA_DOLARES', dFDV, 'Dólares del fondo corregidos')]),
    ...reexpresar(apertura.antes, apertura.despues),
    ...(corte ? reexpresar(corte.antes, corte.despues) : []),
  ];

  const patch: Partial<SesionCaja> = {
    fondoInicial: centavos(nuevo.cordobas),
    fondoDolares: centavos(nuevo.dolares),
    diferenciaApertura: centavos(apertura.despues),
    ...(todoDeGeneral ? { deGeneral: centavos(nuevo.cordobas), esperadoApertura: centavos(nuevo.cordobas) } : {}),
    ...(cerrada
      ? {
          diferenciaCordobas: centavos((s.diferenciaCordobas ?? 0) - dF),
          diferenciaDolares: centavos((s.diferenciaDolares ?? 0) - dFD),
        }
      : { dolaresContables: centavos((s.dolaresContables ?? 0) + dFDV) }),
  };

  return {
    patch,
    apertura,
    corte,
    siguiente: null,
    general,
    aGeneral: { cordobas: 0 - dG, dolares: 0 - dFD, valor, contada: general !== null },
    aPorRecibir: NADA,
    lineas,
  };
}

/**
 * What correcting a till's corte to `nuevo` does (`caja.corte_corregido`): córdobas and dollars
 * (USD cents) really counted, and córdobas really left in the drawer.
 */
export function corregirCorte(
  caja: CajaACorregir,
  nuevo: { cordobas: number; dolares: number; queda: number },
  config: Settings,
): CorreccionDeConteo | Rechazo {
  const no = (por: PorQueElConteoNo, rechazo: string): Rechazo => ({ rechazo, por });
  const s = caja.sesion;
  if (esCamion(s.ubicacion)) return no('deRuta', 'La caja de una ruta se cuadra al liquidarla');
  if (s.estado !== 'cerrada' || caja.totalCorte === null) return no('sinCorte', 'Esa caja todavía no se ha cerrado');
  if (nuevo.queda > nuevo.cordobas) return no('quedaDeMas', 'No se puede dejar en la gaveta más de lo que se contó');

  const dC = nuevo.cordobas - (s.conteoCordobas ?? 0);
  const dD = nuevo.dolares - (s.conteoDolares ?? 0);
  const dQ = nuevo.queda - (s.queda ?? 0);
  if (dC === 0 && dD === 0 && dQ === 0) return no('igual', 'Ese ya es el conteo del corte');
  if (dQ !== 0 && caja.relevada && !caja.siguiente) {
    return no('relevada', 'Esa gaveta ya se abrió otra vez: la corrección lleva la apertura siguiente');
  }

  // What it handed over: the córdobas over what it left, and every dollar.
  const dE = dC - dQ;
  const dDV = usdToCordobas(centavos(dD), fxRate(config.fxTasaOficial));
  const destino = caja.destinoCorte ?? 'porRecibir';
  const recibida = (s.porRecibir ?? 0) < (s.entregado ?? 0) || (s.porRecibirDolares ?? 0) < (s.entregadoDolares ?? 0);
  if (destino === 'porRecibir' && (dE !== 0 || dD !== 0) && recibida) {
    return no('recibida', 'El dueño ya recibió lo que entregó esa caja, y lo contó: ese conteo ya encontró lo que había');
  }

  const corte = { antes: caja.totalCorte, despues: caja.totalCorte + dC + dDV };
  const enCorte = explicadoDeMas(Math.max(-corte.despues, 0), caja.explicadoCorte, 'el corte');
  if (enCorte) return enCorte;

  // The drawer opened again since: the opening that took it over moves with what was left.
  const abrio = dQ !== 0 ? caja.siguiente : null;
  const otra = abrio ? siguienteCorregida(abrio.sesion, dQ) : null;
  if (abrio && otra) {
    const enLaSiguiente = explicadoDeMas(Math.max(-otra.despues, 0), abrio.explicado, 'la apertura siguiente');
    if (enLaSiguiente) return enLaSiguiente;
  }

  const valor = dE + dDV;
  const general = destino === 'general' ? alGeneral(valor, caja.generalCorte) : null;
  if (general && 'rechazo' in general) return general;
  const lineas: JournalLine[] = [
    // The drawer keeps what was really left in it; the rest went where the corte took it. Opened
    // again since, it holds what that opening counted, and does not move.
    ...linea('CAJA_CORDOBAS', dQ + (otra?.gaveta ?? 0), 'Lo que queda en la gaveta, corregido'),
    ...(destino === 'general' ? lineasAlGeneral(valor, general) : linea('EFECTIVO_POR_RECIBIR', valor, 'Lo entregado, corregido')),
    ...reexpresar(corte.antes, corte.despues),
    ...(otra ? reexpresar(otra.antes, otra.despues) : []),
  ];

  const patch: Partial<SesionCaja> = {
    conteoCordobas: centavos(nuevo.cordobas),
    conteoDolares: centavos(nuevo.dolares),
    diferenciaCordobas: centavos((s.diferenciaCordobas ?? 0) + dC),
    diferenciaDolares: centavos((s.diferenciaDolares ?? 0) + dD),
    queda: centavos(nuevo.queda),
    entregado: centavos((s.entregado ?? 0) + dE),
    entregadoDolares: centavos(nuevo.dolares),
    ...(destino === 'porRecibir'
      ? {
          porRecibir: centavos((s.porRecibir ?? 0) + dE),
          porRecibirDolares: centavos((s.porRecibirDolares ?? 0) + dD),
          porRecibirValor: centavos((s.porRecibirValor ?? 0) + dDV),
        }
      : {}),
  };

  const movido: Movido = { cordobas: dE, dolares: dD, valor };
  return {
    patch,
    apertura: { antes: s.diferenciaApertura ?? 0, despues: s.diferenciaApertura ?? 0 },
    corte,
    siguiente: abrio && otra ? { sesionId: abrio.sesion.id, aperturaId: abrio.id, patch: otra.patch, antes: otra.antes, despues: otra.despues } : null,
    general,
    aGeneral: destino === 'general' ? { ...movido, contada: general !== null } : { ...NADA, contada: false },
    aPorRecibir: destino === 'porRecibir' ? movido : NADA,
    lineas,
  };
}

/**
 * The opening that took over a drawer, once what the corte before it left moves by `dQ`. It was
 * held against what was left, and is now held against what really was: what it found over or short
 * moves the other way. What it counted and what it brought from general cash stand, so the drawer's
 * account moves back by what the corte's moved (`gaveta`, −dQ). Even one that took its whole count
 * as the float from general cash: correcting it moves the float (`corregirApertura`), this does
 * not, so two corrections of what was left come to what one would.
 */
function siguienteCorregida(b: SesionCaja, dQ: number) {
  const fondo = b.deGeneral ?? 0;
  const dejado = Math.max((b.esperadoApertura ?? fondo) - fondo, 0);
  const esperado = Math.max(dejado + dQ, 0) + fondo;
  const antes = b.diferenciaApertura ?? 0;
  const despues = b.fondoInicial - esperado;
  return {
    patch: { esperadoApertura: centavos(esperado), diferenciaApertura: centavos(despues) } satisfies Partial<SesionCaja>,
    antes,
    despues,
    gaveta: despues - antes,
  };
}

/**
 * What an event in the log is as one of a till's counts (`Reader.conteoDeCaja`): its opening or its
 * corte, by its till, who, when, and its place in the log. Null for any other.
 */
export function comoConteoDeCaja(e: Envelope | StoredEnvelope): ConteoDeCaja | null {
  if (e.type !== 'caja.abierta' && e.type !== 'caja.cerrada') return null;
  const sesionId = (e.payload as { sesionId?: unknown } | null)?.sesionId;
  if (typeof sesionId !== 'string') return null;
  return { tipo: e.type, sesionId, actorId: e.actorId, clientTs: e.clientTs, lugar: lugarEnElLog(e) };
}

/**
 * Whether a count in the log is the one its till stood on: the opening that opened it, the corte
 * that closed it. A refused one, sent twice or after another, names the same till and is not.
 */
export function esElConteo(conteo: ConteoDeCaja, sesion: SesionCaja): boolean {
  const mismo = (a: string | null | undefined) => a != null && Date.parse(a) === Date.parse(conteo.clientTs);
  if (conteo.tipo === 'caja.abierta') return sesion.usuarioId === conteo.actorId && mismo(sesion.abiertaEn);
  return sesion.estado === 'cerrada' && (sesion.cerradaPor == null || sesion.cerradaPor === conteo.actorId) && mismo(sesion.cerradaEn);
}

/**
 * What the books know of a till to correct one of its counts (`CajaACorregir`). `siguiente`, the
 * opening of the shift that took over the drawer since the corte, when the caller names it.
 * `hasta`, the correction's own place in the log: general cash counted after it is not what it
 * found (a replay holds what came later); the sheet, before there is one, looks at everything.
 */
export async function datosDeLaCaja(
  read: Reader,
  config: Settings,
  sesion: SesionCaja,
  apertura: { id: string; lugar: number } | null,
  corte: { id: string; lugar: number } | null,
  siguiente: { id: string; lugar: number } | null = null,
  hasta = Infinity,
): Promise<CajaACorregir> {
  const asientosCorte = corte ? await read.asientosDeEvento(corte.id) : [];
  const relevo = corte ? await read.relevoDe(sesion.id) : undefined;
  return {
    sesion,
    lugarApertura: apertura?.lugar ?? null,
    lugarCorte: corte?.lugar ?? null,
    totalCorte: corte ? (config.conteosCorregidos[corte.id] ?? totalDeAsientos(asientosCorte)) : null,
    explicadoApertura: apertura ? await explicadoDe(read, apertura.id) : 0,
    explicadoCorte: corte ? await explicadoDe(read, corte.id) : 0,
    destinoCorte: corte ? destinoDelCorte(sesion, asientosCorte) : null,
    generalApertura: apertura ? await conteoAlcanzado(read, config, { general: true }, apertura.lugar, hasta) : null,
    generalCorte: corte ? await conteoAlcanzado(read, config, { general: true }, corte.lugar, hasta) : null,
    relevada: Boolean(relevo),
    siguiente: relevo && siguiente ? { sesion: relevo, ...siguiente, explicado: await explicadoDe(read, siguiente.id) } : null,
  };
}

/** The effects of a correction: the till's row, each count's total as it stands, and the entry. */
function efectos(
  c: CorreccionDeConteo,
  sesion: SesionCaja,
  config: Settings,
  ids: { apertura: string | null; corte: string | null },
  event: AnyDomainEvent,
  concepto: string,
): Effect[] {
  const rev = orderKey(event);
  const cambian: Record<string, number> = {};
  if (ids.apertura && c.apertura.despues !== c.apertura.antes) cambian[ids.apertura] = c.apertura.despues;
  if (ids.corte && c.corte && c.corte.despues !== c.corte.antes) cambian[ids.corte] = c.corte.despues;
  if (c.siguiente && c.siguiente.despues !== c.siguiente.antes) cambian[c.siguiente.aperturaId] = c.siguiente.despues;
  Object.assign(cambian, corregidos(c.general));
  const fx: Effect[] = [{ kind: 'sesion.patch', id: sesion.id, rev, patch: c.patch }];
  if (c.siguiente) fx.push({ kind: 'sesion.patch', id: c.siguiente.sesionId, rev, patch: c.siguiente.patch });
  if (Object.keys(cambian).length > 0) {
    fx.push({ kind: 'config.set', clave: 'conteosCorregidos', valor: { ...config.conteosCorregidos, ...cambian }, rev });
  }
  fx.push({ kind: 'asiento', entry: entry(event.id, fechaEnNicaragua(event.clientTs), concepto, c.lineas) });
  return fx;
}

export async function reduceConteos(event: AnyDomainEvent, read: Reader): Promise<ApplyResult | null> {
  if (event.type !== 'caja.apertura_corregida' && event.type !== 'caja.corte_corregido') return null;
  const no = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });
  const p = event.payload;

  const conteo = await read.conteoDeCaja(p.eventoId);
  const tipo = event.type === 'caja.apertura_corregida' ? 'caja.abierta' : 'caja.cerrada';
  if (!conteo || conteo.tipo !== tipo) {
    return no(tipo === 'caja.abierta' ? 'Eso no fue la apertura de una caja' : 'Eso no fue el corte de una caja');
  }
  const sesion = await read.sesionCaja(conteo.sesionId);
  if (!sesion || !esElConteo(conteo, sesion)) {
    return no(tipo === 'caja.abierta' ? 'Esa no fue la apertura que abrió la caja' : 'Ese no fue el corte que cerró la caja');
  }
  const config = await read.config();

  if (event.type === 'caja.apertura_corregida') {
    const q = event.payload;
    // Closed, its corte comes along: what that count found moves with the opening.
    let corte: { id: string; lugar: number } | null = null;
    if (sesion.estado === 'cerrada') {
      const delCorte = q.corteId ? await read.conteoDeCaja(q.corteId) : null;
      if (!delCorte || delCorte.tipo !== 'caja.cerrada' || delCorte.sesionId !== sesion.id || !esElConteo(delCorte, sesion)) {
        return no('Esa caja ya se cerró: la corrección lleva su corte');
      }
      corte = { id: q.corteId!, lugar: delCorte.lugar };
    }
    const caja = await datosDeLaCaja(read, config, sesion, { id: q.eventoId, lugar: conteo.lugar }, corte, null, lugarEnElLog(event));
    const c = corregirApertura(caja, { cordobas: q.fondoInicial, dolares: q.fondoDolares }, config);
    if ('rechazo' in c) return no(c.rechazo);
    const fx = efectos(c, sesion, config, { apertura: q.eventoId, corte: corte?.id ?? null }, event, `Apertura de caja corregida: ${q.motivo}`);
    const razon = noCuadraLaCorreccion(fx, deLaCorreccion(c, caja, config, { apertura: q.eventoId, corte: corte?.id ?? null }));
    return razon ? no(`La corrección no cuadra (${razon}): no se asentó`) : { effects: fx, rejections: [] };
  }

  const q = event.payload;
  // Opened again since, the opening that took the drawer over comes along: what was left moves it.
  let siguiente: { id: string; lugar: number } | null = null;
  if (q.aperturaSiguienteId) {
    const relevo = await read.relevoDe(sesion.id);
    const suya = await read.conteoDeCaja(q.aperturaSiguienteId);
    if (!relevo || !suya || suya.tipo !== 'caja.abierta' || suya.sesionId !== relevo.id || !esElConteo(suya, relevo)) {
      return no('Esa no fue la apertura que siguió a ese corte');
    }
    siguiente = { id: q.aperturaSiguienteId, lugar: suya.lugar };
  }
  const caja = await datosDeLaCaja(read, config, sesion, null, { id: q.eventoId, lugar: conteo.lugar }, siguiente, lugarEnElLog(event));
  const c = corregirCorte(caja, { cordobas: q.conteoCordobas, dolares: q.conteoDolares, queda: q.queda }, config);
  if ('rechazo' in c) return no(c.rechazo);
  const fx = efectos(c, sesion, config, { apertura: null, corte: q.eventoId }, event, `Corte de caja corregido: ${q.motivo}`);
  const razon = noCuadraLaCorreccion(fx, deLaCorreccion(c, caja, config, { apertura: null, corte: q.eventoId }));
  return razon ? no(`La corrección no cuadra (${razon}): no se asentó`) : { effects: fx, rejections: [] };
}

/**
 * What a correction is held to before it is booked (`noCuadraLaCorreccion`): the tills it changes as
 * they were, and the counts it changes with what each found before.
 */
function deLaCorreccion(
  c: CorreccionDeConteo,
  caja: CajaACorregir,
  config: Settings,
  ids: { apertura: string | null; corte: string | null },
): Parameters<typeof noCuadraLaCorreccion>[1] {
  const conteos: ConteoQueCambia[] = [
    ...(ids.apertura ? [{ id: ids.apertura, cuentas: DE_LA_CAJA, antes: c.apertura.antes }] : []),
    ...(ids.corte && c.corte ? [{ id: ids.corte, cuentas: DE_LA_CAJA, antes: c.corte.antes }] : []),
    ...(c.siguiente ? [{ id: c.siguiente.aperturaId, cuentas: DE_LA_CAJA, antes: c.siguiente.antes }] : []),
    ...(c.general && !c.general.inicial ? [{ id: c.general.id, cuentas: DEL_GENERAL, antes: c.general.antes }] : []),
  ];
  return {
    sesiones: [caja.sesion, ...(caja.siguiente ? [caja.siguiente.sesion] : [])],
    relevadas: new Set(caja.relevada ? [caja.sesion.id] : []),
    conteos,
    corregidos: config.conteosCorregidos,
  };
}
